import { useEffect, type ReactNode } from 'react';
import { XIcon } from '@phosphor-icons/react';

/**
 * Modal for the short create-and-edit forms. Escape, the close button, and the
 * backdrop all dismiss it; anything longer than a screenful belongs on its own
 * route instead.
 */
export function Dialog({
  title,
  onClose,
  children,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <button
        type="button"
        aria-label="Dialog schließen"
        onClick={onClose}
        className="fixed inset-0 bg-black/45"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`enter relative w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} rounded-lg border border-line bg-raised shadow-[var(--shadow-pop)]`}
      >
        <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
          <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="-mr-1.5 rounded-md p-1.5 text-ink-3 transition-colors hover:bg-hover hover:text-ink"
          >
            <XIcon size={17} />
          </button>
        </header>
        <div className="px-5 py-5">{children}</div>
      </div>
    </div>
  );
}
